/**
 * LoginScreen — single typed sign-in. No voice, no child/adult fork.
 * One identifier (username OR email) + password with show toggle + remember.
 */
import { useEffect, useState } from "react";
import { ChromeTokens } from "../chrome/tokens";
import { Icon } from "../chrome/Icon";
import { TrustDeviceToggle } from "../chrome/TrustDeviceToggle";
import { API_BASE, adoptNativeRefreshToken, clientHeaders, credentialsMode, nativeAuthFields, setAccessToken, device } from "../api";
import { accountIdFromLogin, useAuth } from "../hooks/useAuth";
import { getSilhouetteUrls } from "../avatar/avatarUrlResolver";
import { DeviceProfileCache } from "./DeviceProfileCache";
import { IntentRibbon } from "../share/IntentRibbon";
import { noteLoginNextUrl, useIntent } from "../share/useIntent";

const CRED_KEY = "wbm:child-creds";

/** Save child credentials for auto-login on this device. */
export function saveChildCredentials(childId: string, username: string, passphrase: string) {
  try {
    const existing = JSON.parse(localStorage.getItem(CRED_KEY) || "{}");
    existing[childId] = { username, passphrase };
    localStorage.setItem(CRED_KEY, JSON.stringify(existing));
  } catch { /* ignore */ }
}

/** Get saved credentials for a child. */
export function getSavedChildCredentials(childId: string): { username: string; passphrase: string } | null {
  try {
    const existing = JSON.parse(localStorage.getItem(CRED_KEY) || "{}");
    return existing[childId] ?? null;
  } catch { return null; }
}

/** Remove saved credentials for a child. */
export function clearSavedChildCredentials(childId: string) {
  try {
    const existing = JSON.parse(localStorage.getItem(CRED_KEY) || "{}");
    delete existing[childId];
    localStorage.setItem(CRED_KEY, JSON.stringify(existing));
  } catch { /* ignore */ }
}

interface LoginScreenProps {
  onAuthenticated: (session: { type: "child"; childId: string; displayName: string } | { type: "parent" }) => void;
  onSignup: () => void;
}

export function LoginScreen({ onAuthenticated, onSignup }: LoginScreenProps) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [trust, setTrust] = useState(true);
  const { refreshSession } = useAuth();
  // A share link's intent survives the switch from sign-up to sign-in (U10).
  const intentState = useIntent();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setNeedsVerification(false);
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...clientHeaders() },
        credentials: credentialsMode(),
        body: JSON.stringify({
          identifier: identifier.trim(),
          password,
          personal_device: trust,
          ...nativeAuthFields(),
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        const detail = (body as { detail?: string }).detail || "";
        if (detail === "email_not_verified") {
          setNeedsVerification(true);
        } else {
          setError("Invalid username/email or password.");
        }
        return;
      }
      const data = await resp.json();
      if (data.access_token) {
        // The HttpOnly access cookie set by the backend response is the
        // canonical credential (R15.3). We keep the in-memory copy via
        // setAccessToken for the Bearer fallback path (mobile native,
        // mid-flight requests); we deliberately do NOT mirror it back
        // into localStorage where XSS could read it.
        setAccessToken(data.access_token);
      }
      adoptNativeRefreshToken(data);
      noteLoginNextUrl(data);
      DeviceProfileCache.upsert({
        account_id: "self",
        username: data.username || identifier.split("@")[0],
        avatar_url: data.avatar_url || getSilhouetteUrls("self").portrait_128,
        account_type: data.account_type || "adult",
        family_id: data.family_id || null,
        last_used_at: Date.now(),
        trusted_on_this_device: trust,
      });
      // Server-side device trust is now authoritative for the profile picker.
      // Persist it on this device when checked, or clear it when not — so a
      // sign-in with the toggle off forgets a previously trusted device.
      try {
        if (trust) await device.trust();
        else await device.forget();
      } catch { /* non-fatal */ }
      await refreshSession();
      onAuthenticated(
        data.account_type === "child"
          ? { type: "child", childId: accountIdFromLogin(data), displayName: data.username || "" }
          : { type: "parent" }
      );
    } catch {
      setError("Something went wrong. Check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await fetch(`${API_BASE}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: identifier.trim() }),
      });
      setError("Verification email sent! Check your inbox.");
    } catch {
      setError("Could not resend. Try again later.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <IntentRibbon intent={intentState.intent} sourceTitle={intentState.sourceTitle} />
        <img src="/logo/wordmark-color-small.png" alt="world by me" style={{ height: 36, width: 'auto', display: 'block', margin: '0 auto 8px' }} />
        <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700, color: ChromeTokens.color.ink, textAlign: 'center' }}>Welcome back</h2>

        <InvalidationBanner />

        {needsVerification ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'center' }}>
            <p style={{ color: ChromeTokens.color.ink, fontSize: 14, margin: 0 }}>
              Check your email for a verification link before logging in.
            </p>
            <button onClick={handleResend} disabled={resendLoading} style={pillBtnStyle}>
              {resendLoading ? "Sending..." : "Resend verification email"}
            </button>
            <button onClick={() => setNeedsVerification(false)} style={linkStyle}>← Try again</button>
          </div>
        ) : (
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="Username or email"
              autoFocus
              autoComplete="username"
              style={inputStyle}
              required
            />
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                style={{ ...inputStyle, paddingRight: 40 }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="wbm-tap-slop"
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: ChromeTokens.color.inkMuted }}
              >
                <Icon name={showPassword ? 'eye-off' : 'eye'} size={18} />
              </button>
            </div>
            {error && <p style={{ color: ChromeTokens.color.danger, fontSize: 13, margin: 0 }}>{error}</p>}
            <TrustDeviceToggle checked={trust} onChange={setTrust} />
            <button type="submit" disabled={loading} style={pillBtnStyle}>
              {loading ? "Logging in..." : "Log in"}
            </button>
          </form>
        )}
      </div>

      <button onClick={onSignup} style={linkStyle}>New here? Sign up</button>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  minHeight: '100vh', gap: 16,
  // 24 all round, more where a phone's notch, Dynamic Island or home indicator
  // needs it; a page taller than the screen (a phone in landscape) scrolls its
  // last control clear of the indicator.
  padding: 'max(24px, var(--safe-top)) max(24px, var(--safe-right)) max(24px, var(--safe-bottom)) max(24px, var(--safe-left))',
  boxSizing: 'border-box',
  background: `linear-gradient(180deg, ${ChromeTokens.color.sky} 0%, ${ChromeTokens.color.base} 100%)`,
};

const cardStyle: React.CSSProperties = {
  // 360 of content + 28 padding each side, as before, but border-box so a
  // 390pt phone's card shrinks to fit instead of scrolling sideways.
  width: '100%', maxWidth: 416, boxSizing: 'border-box', padding: 28, borderRadius: ChromeTokens.radius.lg,
  background: `color-mix(in srgb, ${ChromeTokens.color.base} 92%, transparent)`,
  boxShadow: ChromeTokens.shadow.lifted,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '11px 14px', fontSize: 15, borderRadius: ChromeTokens.radius.md,
  border: `1.5px solid ${ChromeTokens.color.baseMuted}`, background: ChromeTokens.color.base,
  color: ChromeTokens.color.ink, outline: 'none', fontFamily: ChromeTokens.font.ui,
  boxSizing: 'border-box',
};

const pillBtnStyle: React.CSSProperties = {
  width: '100%', padding: '12px 20px', borderRadius: ChromeTokens.radius.pill,
  border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 700,
  background: ChromeTokens.color.sun, color: ChromeTokens.color.ink,
  fontFamily: ChromeTokens.font.ui,
};

const linkStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: ChromeTokens.color.inkMuted,
  fontSize: 14, cursor: 'pointer', minHeight: 44, padding: '0 12px',
};

/**
 * One-time banner shown when the user landed here because their session
 * was invalidated (currently: password changed on another device — set by
 * the AuthProvider's auth:invalidated handler).
 *
 * Reads the reason from sessionStorage on mount and clears it so the banner
 * doesn't reappear after the next login.
 */
function InvalidationBanner() {
  const [reason, setReason] = useState<string | null>(null);
  useEffect(() => {
    try {
      const r = sessionStorage.getItem("auth_invalidated_reason");
      if (r) {
        setReason(r);
        sessionStorage.removeItem("auth_invalidated_reason");
      }
    } catch {
      /* SSR/test */
    }
  }, []);
  if (!reason) return null;
  const message =
    reason === "password_changed_elsewhere"
      ? "Your password was changed on another device — please sign in again."
      : "Your session ended — please sign in again.";
  return (
    <div
      role="status"
      style={{
        marginBottom: ChromeTokens.spacing.md,
        padding: `${ChromeTokens.spacing.sm}px ${ChromeTokens.spacing.md}px`,
        borderRadius: ChromeTokens.radius.md,
        background: ChromeTokens.color.baseMuted,
        color: ChromeTokens.color.ink,
        fontSize: 13,
        textAlign: "center",
      }}
    >
      {message}
    </div>
  );
}
